<template>
  <procedure-page
    ref="procedurePage"
    :id="$route.params.id"
    :procedure="procedure"
    :load-func="load"
    :loading-dialog-text="loadingDialogText"
    :status="procedure.step"
    :types="types"
    :isClosed="procedure.isClosed"
    @update:procedureGoodName="procedure.goodName = $event"
    @update:procedureType="procedure.type = $event"
  >
    <decision-grid
        v-if="showDecisionGrid"
        :procedureId="procedure.id"
        :procedure-type="procedure.type"
        :status="procedure.step.code"
        :applications="applications"
        :hasManyRounds="hasManyRounds"
        :isNds="procedure.isNds"
    />
    <decision-positions-grid
      v-if="showDecisionPositionsGrid"
      :applications="applications"
      :positions="procedure.positions"
      :procedure="procedure"
      :hasManyRounds="hasManyRounds"
      v-model:selected-positions="selectedCells"
    />
    <div class="d-flex justify-space-between">
      <additional-actions :procedure="procedure" :additional-actions="additionalActions" />
      <div class="d-flex justify-end ga-3">
        <v-btn
            flat
            color="primary"
            density="comfortable"
            variant="outlined"
            @click="goToProcedures"
        >{{ $t('Отмена') }}</v-btn>
        <v-btn
            flat
            color="primary"
            density="comfortable"
            @click="confirm($route.params.id)"
        >{{ $t('Завершить процедуру') }}</v-btn>
      </div>
    </div>
  </procedure-page>
  <ConfirmDialog
      ref="confirmCancellation"
      :disabled="!cancellationIsValid"
  >
    <template v-slot:content>
      <CancellationReasonsDecision
          v-model:reasons="cancellationReasons"
          v-model:isValid="cancellationIsValid"
      />
    </template>
  </ConfirmDialog>
</template>

<script setup lang="ts">
import {computed, reactive, ref} from 'vue';
import {useRouter} from 'vue-router';
import { toast } from '@/lib/toast';
import api from '../api';

import ProcedurePage from "../components/ProcedurePage.vue";
import DecisionGrid from "../components/application/DecisionGrid.vue";
import DecisionPositionsGrid from "../components/application/DecisionPositionsGrid.vue";
import AdditionalActions from "@/components/procedure/AdditionalActions.vue";
import AdditionalAction from "@/interfaces/AdditionalAction";
import ProcedureStep from "@/components/ProcedureStep";
import { useProcedureSummingStore } from "@/stores/procedureSummingStore";
import { useLibraryStore } from "@/stores/library";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import CancellationReasonsDecision from '@/components/procedure/CancellationReasonsDecision.vue';

interface ProcedurePosition {
  id: number;
  name: string;
  measureId: number;
  measure: string;
  quantity: number;
  description: string | null;
}

interface Procedure {
  id?: number
  registryNumber?: string
  type?: string
  goodName?: string
  subject?: string
  statusText: string
  startMaxPrice?: string
  applicationDeadline?: null
  summingStart?: null
  shippingCondition?: string
  publishedAt?: null
  organizerName?: string
  isWithoutStartMaxPrice: boolean
  isNds?: boolean
  isClosed: boolean
  withPositions: boolean
  positions: ProcedurePosition[]
  partOfVolume: boolean
  step: {
    name: string
    code: string
  },
  [key: string]: any
}

interface Application {
  isAccepted?: boolean
  isWinner?: boolean
  [key: string]: any
}

const procedurePage = ref();
const loadingDialogText = ref<string | null>();
const procedure = reactive<Procedure>({
  id: null,
  registryNumber: '',
  type: '',
  goodName: '',
  subject: '',
  statusText: '',
  startMaxPrice: '',
  applicationDeadline: null,
  shippingCondition: '',
  publishedAt: null,
  organizerName: '',
  step: { name: '', code: '' },
  isWithoutStartMaxPrice: false,
  isNds: null,
  isClosed: false,
  withPositions: false,
  positions: [],
  partOfVolume: false,
});
const applications = ref<Application[]>([]);
const additionalActions = ref<AdditionalAction[]>([]);
const types = ref<{
  title: string
  value: string
}[]>([]);
const hasManyRounds = ref(false);
interface ApplicationItemDTO {
  id: number
  quantity: number
}
interface ApplicationDTO {
  id: number
  selectedPositions: ApplicationItemDTO[]
}

type SelectedCells = Record<number, Record<number, number>>;

const selectedCells = ref<SelectedCells>({});

const confirmCancellation = ref(null);
const cancellationReasons = ref<{
  id: number,
  explanation: string,
}[]>([]);
const cancellationIsValid = ref<boolean>(false);

const winners = computed<ApplicationDTO[] | null>(() => {
  const apps = applications.value.filter(({ isWinner }) => isWinner);

  if (apps.length === 0) return null;

  return apps.map(app => {
    const selectedPositions: ApplicationItemDTO[] = Object.entries(selectedCells.value)
      .filter(([_, appMap]) => app.id in appMap)
      .map(([positionId, appMap]) => {
        const quantity = appMap[app.id];
        return {
          id: Number(positionId),
          quantity
        };
      });

    return {
      id: app.id,
      isWinner: app.isWinner,
      selectedPositions
    };
  });
});

const procedureSummingStore = useProcedureSummingStore();
const libraryStore = useLibraryStore();

const { REVIEW, SUMMING } = ProcedureStep;
const showDecisionGrid = computed(() =>
  procedureSummingStore.showDecisionGrid ||
  (
    procedure.id &&
    (!procedure.withPositions || procedure.step.code === REVIEW)
  )
);

const showDecisionPositionsGrid = computed(() =>
  procedure.id &&
  procedure.withPositions &&
  procedure.step.code === SUMMING &&
  !procedureSummingStore.showDecisionGrid
);

const load = async procedureId => {
  const resp = await api.get(`/procedures/${procedureId}/summarize`);
  Object.assign(procedure, resp.procedure);
  applications.value = resp.applications;
  additionalActions.value = resp.additionalActions;
  hasManyRounds.value = resp.hasManyRounds;
  types.value = resp.types.map((elem) => ({
    title: elem.name,
    value: elem.code,
  }));

  selectedCells.value = libraryStore.callFunctionByCode('autoSelectBestApplication', {
    procedure: procedure,
    applications: applications
  }).selected ?? {};

  libraryStore.callActionByCode('initProcedureSummingOptions', {
    procedureType: procedure.type,
    procedureStatus: procedure.step.code
  });
};

const router = useRouter();
const goToProcedures = () => router.push({ name: 'procedures.all' });
const goToCompleted = () => router.push({
  name: 'procedures.completed',
  params: { id: procedure.id },
});

const confirm = async procedureId => {
  const approve = await (
    winners.value
      ? (procedurePage.value.confirm.open(
        'Подведение итогов',
        'Вы действительно хотите разместить результаты подведения итогов',
      ))
      : (confirmCancellation.value.open('Завершить процедуру'))
  );
  if (approve) {

    loadingDialogText.value = 'Подводятся итоги';
    try {
      const formData = new FormData()
      const data = winners.value
        ? { winners: winners.value, status: 'yes' }
        : {
          status: 'no',
          reasons: cancellationReasons.value.map((reason) => {
            return {
              id: reason.id,
              explanation: reason.explanation,
            };
          }),
        };
      formData.append('data', JSON.stringify(data));
      await api.post(`/procedures/${procedureId}/summarize`, formData);
      toast.info('Процедура обновлена успешно');
      if (winners.value) {
        goToCompleted();
      } else {
        goToProcedures();
      }
    } catch (e) {
      toast.error(e.message);
    }
    loadingDialogText.value = null;
  }
};
</script>

<style scoped lang="scss">
button {
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  padding: 10px 20px;
  height: auto !important;
  border-radius: 6px;
}
</style>
